import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  minTouchTarget,
  radius,
  semanticColor,
  shadow,
  space,
  typeStyle,
  zIndex,
} from '@beautyburst/tokens';
import type { CustomerApiClient } from './api-client.ts';
import { customerHref } from './base-path.ts';
import { CustomerApiError } from './http-client.ts';

type CartAddFeedbackState = 'sign-in' | 'generic' | null;
type CartAddInput = { listingId: string; quantity: number };

interface CartAddFeedbackContextValue {
  readonly feedback: CartAddFeedbackState;
  readonly dismiss: () => void;
  readonly addToBag: (
    client: Pick<CustomerApiClient, 'cart'> | undefined,
    input: CartAddInput,
  ) => Promise<boolean>;
}

const CartAddFeedbackContext = createContext<CartAddFeedbackContextValue | null>(null);

async function addItemWithFeedback(
  client: Pick<CustomerApiClient, 'cart'> | undefined,
  input: CartAddInput,
  setFeedback: (feedback: CartAddFeedbackState) => void,
): Promise<boolean> {
  setFeedback(null);
  if (client === undefined) {
    setFeedback('generic');
    return false;
  }
  try {
    await client.cart.addItem(input);
    return true;
  } catch (error) {
    setFeedback(
      error instanceof CustomerApiError && error.code === 'UNAUTHENTICATED' ? 'sign-in' : 'generic',
    );
    return false;
  }
}

/**
 * The browser app owns one cart-feedback region. Product surfaces only dispatch
 * through it, so a new surface cannot accidentally omit visible feedback.
 */
export function CartAddFeedbackProvider({
  children,
  scopeKey,
}: {
  readonly children: ReactNode;
  /** A route or market transition invalidates feedback from the previous customer context. */
  readonly scopeKey?: string;
}) {
  const [feedback, setFeedback] = useState<CartAddFeedbackState>(null);
  const dismiss = useCallback(() => setFeedback(null), []);

  useEffect(() => {
    dismiss();
  }, [dismiss, scopeKey]);

  useEffect(() => {
    if (feedback === null) return;
    // Eight seconds leaves enough time for the assertive announcement and for
    // keyboard or screen-reader users to reach the visible actions.
    const timeout = window.setTimeout(dismiss, 8_000);
    return () => window.clearTimeout(timeout);
  }, [dismiss, feedback]);

  const value = useMemo<CartAddFeedbackContextValue>(
    () => ({
      feedback,
      dismiss,
      addToBag: (client, input) => addItemWithFeedback(client, input, setFeedback),
    }),
    [dismiss, feedback],
  );
  return (
    <CartAddFeedbackContext.Provider value={value}>
      {children}
      <CartAddFeedbackMessage feedback={feedback} onDismiss={dismiss} />
    </CartAddFeedbackContext.Provider>
  );
}

/**
 * Customer cart mutations must handle their own rejection. A visible alert keeps
 * anonymous shoppers from receiving a silent failed click on any product surface.
 */
export function useCartAddFeedback(client?: Pick<CustomerApiClient, 'cart'>) {
  const shared = useContext(CartAddFeedbackContext);
  const [feedback, setFeedback] = useState<CartAddFeedbackState>(null);

  if (shared !== null)
    return {
      addToBag: (input: CartAddInput) => shared.addToBag(client, input),
      dismiss: shared.dismiss,
      feedback: null,
    };

  return {
    addToBag: (input: CartAddInput) => addItemWithFeedback(client, input, setFeedback),
    dismiss: () => setFeedback(null),
    feedback,
  };
}

export function CartAddFeedback({
  feedback,
  onDismiss,
}: {
  feedback: CartAddFeedbackState;
  onDismiss?: () => void;
}) {
  const shared = useContext(CartAddFeedbackContext);
  if (shared !== null) return null;
  return <CartAddFeedbackMessage feedback={feedback} onDismiss={onDismiss ?? (() => {})} />;
}

const feedbackSurfaceStyle: CSSProperties = {
  position: 'fixed',
  zIndex: zIndex.toast,
  top: `max(${space[4]}, env(safe-area-inset-top))`,
  right: `max(${space[4]}, env(safe-area-inset-right))`,
  width: `min(28rem, calc(100vw - ${space[8]}))`,
  maxHeight: `calc(100vh - ${space[8]})`,
  overflowY: 'auto',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'flex-start',
  gap: space[3],
  padding: space[4],
  color: semanticColor.statusDangerText,
  background: semanticColor.statusDangerSurface,
  border: `1px solid ${semanticColor.statusDanger}`,
  borderRadius: radius.md,
  boxShadow: shadow.md,
  ...typeStyle('bodySm'),
};

function CartAddFeedbackMessage({
  feedback,
  onDismiss,
}: {
  feedback: CartAddFeedbackState;
  onDismiss: () => void;
}) {
  if (feedback === null) return null;
  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      data-bb-cart-add-feedback="viewport"
      style={{
        ...feedbackSurfaceStyle,
      }}
    >
      <span style={{ flex: '1 1 auto', paddingTop: space[2] }}>
        {feedback === 'sign-in' ? (
          <>
            Sign in to add items to your bag.{' '}
            <a
              href={customerHref('/sign-in')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: minTouchTarget,
                color: semanticColor.textLink,
                fontWeight: 700,
              }}
            >
              Sign in
            </a>
          </>
        ) : (
          'We could not add this item to your bag. Please try again.'
        )}
      </span>
      <button
        type="button"
        aria-label="Dismiss cart message"
        onClick={onDismiss}
        style={{
          flex: '0 0 auto',
          minWidth: minTouchTarget,
          minHeight: minTouchTarget,
          padding: `0 ${space[2]}`,
          color: semanticColor.textLink,
          background: semanticColor.surfaceWhite,
          border: `1px solid ${semanticColor.borderDefault}`,
          borderRadius: radius.sm,
          cursor: 'pointer',
          ...typeStyle('label'),
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
